import { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCoreData } from '../contexts/CoreDataContext';
import { LoadingScreen } from './LoadingScreen';

interface CoreDataLoaderProps {
  children: React.ReactNode;
}

/**
 * Component that loads core data after authentication
 * Shows loading screen while core data is being fetched
 */
export function CoreDataLoader({ children }: CoreDataLoaderProps) {
  const { isAuthenticated } = useAuth();
  const { isLoaded, isLoading, error, load } = useCoreData();
  const loadAttemptedRef = useRef(false); // Prevent duplicate load attempts in StrictMode

  useEffect(() => {
    if (isAuthenticated && !isLoaded && !isLoading && !loadAttemptedRef.current) {
      loadAttemptedRef.current = true;
      console.log('[CoreDataLoader] User authenticated, loading core data...');
      load();
    }
  }, [isAuthenticated, isLoaded, isLoading, load]);

  // If not authenticated, don't load core data, just show children (signin page)
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If authenticated but core data not loaded yet
  if (!isLoaded && isLoading) {
    return <LoadingScreen message="Loading application data..." />;
  }

  // If there was an error loading core data
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-avemio-dark-bg">
        <div className="max-w-md rounded-lg bg-white dark:bg-avemio-dark-surface p-8 shadow-lg border border-gray-200 dark:border-avemio-dark-border">
          <h1 className="mb-4 text-2xl font-bold text-red-600 dark:text-red-400">Data Loading Error</h1>
          <p className="text-gray-700 dark:text-avemio-dark-text-primary mb-4">{error}</p>
          <button
            onClick={() => load()}
            className="px-4 py-2 bg-avemio-green text-white rounded hover:opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Core data loaded successfully, show the app
  return <>{children}</>;
}

